import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'

import { PENDING_WORKFLOW } from 'citadel-shared/fileManagement.js'
import axios from '../../dataClient/dataClient'
import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import AutocompleteSelect from '../inputFields/AutocompleteSelect'
import BulkFileResultList, { FormTitle, Helper, bulkFileSkipMessages, toSkippedRows } from './BulkFileResultList'

const verbs = { move: 'Move', restore: 'Restore' }
const pastVerbs = { move: 'moved', restore: 'restored' }

export const moveFilesToCategoryDialogStrings = {
  title: (mode, count) => `${verbs[mode]} ${count} ${count === 1 ? 'file' : 'files'}`,
  resultTitle: mode => `${verbs[mode]} files`,
  destinationLabel: 'Destination category',
  createOption: value => `Create category “${value}”`,
  helper: 'Files keep their relative order. Date-ordered categories place them by date; manually ordered categories add them at the end.',
  resultSummary: (mode, movedCount, total, failedCount) =>
    `${movedCount} of ${total} files ${pastVerbs[mode]}. ${failedCount} ${failedCount === 1 ? 'file' : 'files'} could not be ${pastVerbs[mode]}:`,
  cancel: 'Cancel',
  close: 'Close',
  required: 'Destination category is required',
  errors: {
    move: 'There was an error moving the files. Please try again.',
    restore: 'There was an error restoring the files. Please try again.'
  },
  skipMessages: bulkFileSkipMessages,
  skippedFallback: mode => `File could not be ${pastVerbs[mode]}`
}

const moveSchema = yup.object().shape({
  category: yup
    .object()
    .shape({
      id: yup.string(),
      label: yup.string().required(moveFilesToCategoryDialogStrings.required)
    })
    .typeError(moveFilesToCategoryDialogStrings.required)
    .required(moveFilesToCategoryDialogStrings.required)
})

function MoveFilesToCategoryDialog({ open, mode = 'move', foiId, categories = [], files, onClose = () => {}, onMoved = () => {} }) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState()
  const [result, setResult] = useState()
  // Category created by a submit whose move then failed: a retry with the same
  // typed name reuses it instead of POSTing an identically-named duplicate.
  const [createdCategory, setCreatedCategory] = useState()

  const methods = useForm({ resolver: yupResolver(moveSchema) })
  const { handleSubmit, reset } = methods

  function handleClose() {
    reset({ category: null })
    setError(undefined)
    setResult(undefined)
    setCreatedCategory(undefined)
    setIsLoading(false)
    onClose()
  }

  const onError = (errors, e) => console.error('Error', errors, e)

  async function onSubmit(data) {
    setIsLoading(true)
    setError(undefined)

    let destinationId = data.category.id
    if (!destinationId && createdCategory?.name === data.category.label) {
      destinationId = createdCategory.id
    }
    if (!destinationId) {
      try {
        const { data: responseData } = await axios.post('/category', { name: data.category.label, foiId })
        destinationId = responseData.data.id
        setCreatedCategory({ name: data.category.label, id: destinationId })
      } catch (e) {
        console.error('Failed creating category with name', data.category.label, e)
        setError(moveFilesToCategoryDialogStrings.errors[mode])
        setIsLoading(false)
        return
      }
    }

    const fileIds = files.map(f => f.id)
    axios
      .post(`/category/${destinationId}/files/move`, { fileIds })
      .then(response => {
        const { moved, skipped } = response.data.data
        onMoved({ moved, skipped, categoryId: destinationId })
        if (skipped.length === 0) {
          handleClose()
          return
        }
        setResult({
          movedCount: moved.length,
          total: fileIds.length,
          skipped: toSkippedRows(skipped, files, moveFilesToCategoryDialogStrings.skippedFallback(mode))
        })
        setIsLoading(false)
      })
      .catch(e => {
        console.error('Failed moving files to category', destinationId, e)
        const pendingWorkflow = e.response?.status === 409 && e.response?.data?.code === PENDING_WORKFLOW
        setError((pendingWorkflow && e.response.data.message) || moveFilesToCategoryDialogStrings.errors[mode])
        setIsLoading(false)
      })
  }

  return (
    <StyledDialog fullWidth maxwidth="600px" open={open} onClose={isLoading ? undefined : handleClose} keepMounted={false}>
      <StyledDialogContent>
        {result ? (
          <BulkFileResultList
            title={moveFilesToCategoryDialogStrings.resultTitle(mode)}
            summary={moveFilesToCategoryDialogStrings.resultSummary(mode, result.movedCount, result.total, result.skipped.length)}
            skipped={result.skipped}
            closeLabel={moveFilesToCategoryDialogStrings.close}
            onClose={handleClose}
          />
        ) : (
          <FormProvider {...methods}>
            <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
              <FormTitle>{moveFilesToCategoryDialogStrings.title(mode, files.length)}</FormTitle>
              <AutocompleteSelect
                id="category"
                label={moveFilesToCategoryDialogStrings.destinationLabel}
                required
                options={categories}
                disabled={isLoading}
                optionIcon={<FolderOutlinedIcon />}
                createOptionLabel={moveFilesToCategoryDialogStrings.createOption}
              />
              <Helper>{moveFilesToCategoryDialogStrings.helper}</Helper>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  {moveFilesToCategoryDialogStrings.cancel}
                </SecondaryButton>
                <Button type="submit" loading={isLoading}>
                  {moveFilesToCategoryDialogStrings.title(mode, files.length)}
                </Button>
              </FlexEndContainer>
            </PaddedForm>
          </FormProvider>
        )}
      </StyledDialogContent>
    </StyledDialog>
  )
}

MoveFilesToCategoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['move', 'restore']),
  foiId: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string.isRequired
    })
  ).isRequired,
  onClose: PropTypes.func,
  onMoved: PropTypes.func
}

export default MoveFilesToCategoryDialog
